"use client";

import { useState, useRef } from "react";
import Link from "next/link";
import { FaSearch, FaBoxOpen } from "react-icons/fa";
import ProductCard from "@/components/ProductCard";

function matchesQuery(product, q) {
  const lower = q.toLowerCase();
  if (product.name?.toLowerCase().includes(lower)) return true;
  if (product.description?.toLowerCase().includes(lower)) return true;
  if (product.aliases?.some((a) => a.toLowerCase().includes(lower))) return true;
  return false;
}

export default function GetAQuoteClient({ products }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  const trimmed = query.trim();
  const results = trimmed
    ? products.filter((p) => matchesQuery(p, trimmed))
    : [];
  const hasQuery = trimmed.length > 0;
  const noResults = hasQuery && results.length === 0;

  return (
    <div className="flex flex-col min-h-screen bg-slate-50">

      {/* ── Hero search bar ── */}
      <section className="bg-gradient-to-br from-emerald-800 via-emerald-700 to-teal-700 py-16 md:py-24">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 tracking-tight">
            Get a Quote
          </h1>
          <p className="text-emerald-100 text-base md:text-lg mb-10 max-w-xl mx-auto leading-relaxed">
            Search our compounded medication catalog to find pricing. Select a product to view all available strengths, forms, and quantities.
          </p>

          <div className="max-w-2xl mx-auto">
            <div className="flex items-stretch rounded-2xl overflow-hidden shadow-2xl">
              <div className="relative flex-1">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="e.g. Naltrexone, Thyroid, Minoxidil…"
                  className="w-full pl-11 pr-4 py-4 text-base text-slate-900 bg-white focus:outline-none placeholder:text-slate-400"
                  autoFocus
                />
              </div>
              <button
                type="button"
                onClick={() => inputRef.current?.focus()}
                className="bg-emerald-500 hover:bg-emerald-400 transition-colors text-white font-bold px-7 text-sm tracking-wide"
              >
                Search
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Results area ── */}
      <section className="flex-1 container mx-auto px-4 py-12">

        {/* Empty state — no query yet */}
        {!hasQuery && (
          <div className="text-center text-slate-400 mt-8">
            <FaBoxOpen className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p className="text-lg font-medium">Start typing above to search our product catalog.</p>
          </div>
        )}

        {/* Results grid */}
        {results.length > 0 && (
          <>
            <p className="text-sm text-slate-500 mb-6">
              {results.length} result{results.length !== 1 ? "s" : ""} for <strong className="text-slate-700">&ldquo;{trimmed}&rdquo;</strong>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {results.map((product) => (
                <ProductCard key={product.slug} product={product} />
              ))}
            </div>
          </>
        )}

        {/* No results */}
        {noResults && (
          <div className="max-w-lg mx-auto text-center mt-8">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10">
              <FaBoxOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <h2 className="text-lg font-bold text-slate-800 mb-2">No products found</h2>
              <p className="text-slate-500 text-sm mb-6 leading-relaxed">
                We couldn&rsquo;t find a match for <strong>&ldquo;{trimmed}&rdquo;</strong> in our catalog. We may be able to compound it for you — reach out and our pharmacists will get back to you with pricing.
              </p>
              <Link
                href="/contact-us/"
                className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold px-6 py-3 rounded-xl transition-colors shadow"
              >
                Contact Us for a Custom Quote →
              </Link>
            </div>
          </div>
        )}

      </section>
    </div>
  );
}
